import { useEffect, useRef, useState } from "react";
import { Check, Mail, Phone, RefreshCw, Smartphone } from "lucide-react";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useSendOtp } from "@/features/auth/api/use-send-otp";
import { useVerifyOtp } from "@/features/auth/api/use-verify-otp";
import type {
  OtpChannel,
  OtpPreferredLanguage,
} from "@/features/auth/api/dto";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Jeton de preuve de possession du numéro (delta v3.4). Renvoyé par
 * `Otp/verify` sur canal `SMS` / `BOTH` quand `PhoneVerification__KeyBase64`
 * est configuré côté serveur. Null sinon — l'UI doit tolérer ce cas.
 */
export interface OtpPhoneVerificationProof {
  phoneVerificationToken: string;
  phoneVerificationTokenExpiresAt: string | null;
}

interface OtpChallengeProps {
  /** Canal d'envoi — `SMS` / `EMAIL` / `BOTH` (en BOTH : téléphone,email). */
  channel: OtpChannel;
  /** Destinataire. En BOTH : `téléphone,email`. */
  recipientAddress: string;
  /** Langue préférée du gabarit. */
  preferredLanguage?: OtpPreferredLanguage;
  /**
   * Appelé quand la vérification réussit (`isValid=true`).
   * **Delta v3.4** : `proof` porte le jeton de preuve de possession du numéro
   * (canal SMS / BOTH uniquement) ou `null` (canal EMAIL, ou serveur sans
   * `PhoneVerification__KeyBase64`).
   */
  onVerified: (proof: OtpPhoneVerificationProof | null) => void;
  /** Optionnel : partyId / keycloakUserId si déjà connus (post-inscription). */
  partyId?: string | null;
  keycloakUserId?: string | null;
  /** Déclenche automatiquement un premier envoi au montage. Défaut : true. */
  autoSendOnMount?: boolean;
}

/**
 * Challenge OTP (guide v2.3 §6.9) — endpoints publics `Otp/send` + `Otp/verify`
 * du user-service. Compte à rebours de renvoi (défaut 60 s), 6 cases chiffres,
 * tolérance par le backend sur la casse / séparateurs.
 *
 * **ENDPOINTS PUBLICS** : pas de Bearer requis — pensé pour la pré-inscription
 * (vérifier téléphone/email avant de créer le compte Keycloak). Les quotas
 * (délai, horaire, tentatives) sont serveur, le composant n'en reflète que le
 * cooldown de renvoi.
 */
export function OtpChallenge({
  channel,
  recipientAddress,
  preferredLanguage = "FR",
  onVerified,
  partyId,
  keycloakUserId,
  autoSendOnMount = true,
}: OtpChallengeProps) {
  const { t } = useTypedTranslation();
  const tx = (key: string) => t(key as never, {} as never) as string;

  const send = useSendOtp();
  const verify = useVerifyOtp();

  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const sentOnceRef = useRef(false);

  // Compte à rebours du bouton « Renvoyer ».
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const triggerSend = () => {
    send.mutate(
      {
        channel,
        recipientAddress,
        preferredLanguage,
        partyId: partyId ?? null,
        keycloakUserId: keycloakUserId ?? null,
      },
      {
        onSuccess: (data) => {
          notifSilence(tx("auth.otp.feedback.sent"));
          setCooldown(data?.nextSendInSeconds ?? RESEND_COOLDOWN_SECONDS);
        },
        onError: (error) => notifSilenceError(apiErrorHandler(error)),
      }
    );
  };

  // Envoi automatique une seule fois au montage.
  useEffect(() => {
    if (!autoSendOnMount || sentOnceRef.current || !recipientAddress) return;
    sentOnceRef.current = true;
    triggerSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSendOnMount, recipientAddress]);

  const triggerVerify = () => {
    verify.mutate(
      { channel, recipientAddress, code },
      {
        onSuccess: (result) => {
          if (result?.isValid) {
            notifSilence(tx("auth.otp.feedback.verified"));
            const proof: OtpPhoneVerificationProof | null =
              result.phoneVerificationToken
                ? {
                    phoneVerificationToken: result.phoneVerificationToken,
                    phoneVerificationTokenExpiresAt:
                      result.phoneVerificationTokenExpiresAt ?? null,
                  }
                : null;
            onVerified(proof);
          } else {
            notifSilenceError(tx("auth.otp.feedback.invalid"));
            setCode("");
          }
        },
        onError: () => {
          // 400 isValid=false : backend ne distingue pas les cas (sécurité).
          notifSilenceError(tx("auth.otp.feedback.invalid"));
          setCode("");
        },
      }
    );
  };

  const Icon =
    channel === "SMS" ? Smartphone : channel === "EMAIL" ? Mail : Phone;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-5 w-5 text-primary" />
          {tx("auth.otp.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          {tx("auth.otp.description")}
        </p>
        <p className="text-sm">
          <strong className="notranslate">{recipientAddress}</strong>
        </p>
        <div className="flex flex-col gap-2">
          <Label htmlFor="otp-code">{tx("auth.otp.codeLabel")}</Label>
          <Input
            id="otp-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))
            }
            placeholder="000000"
            className="text-center text-2xl tracking-[0.4em] font-mono"
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={cooldown > 0 || send.isPending}
            onClick={triggerSend}
          >
            {send.isPending ? (
              <ButtonSpinner />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {cooldown > 0
              ? tx("auth.otp.resendIn").replace("{{seconds}}", String(cooldown))
              : tx("auth.otp.resend")}
          </Button>
          <Button
            type="button"
            onClick={triggerVerify}
            disabled={code.length !== CODE_LENGTH || verify.isPending}
          >
            {verify.isPending ? (
              <ButtonSpinner />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {tx("auth.otp.verify")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
